import type { CasePriority, CaseStatus, TestCaseSortField } from '@tms/shared';
import { CASE_PRIORITIES, CASE_STATUSES } from '@tms/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowDownUp,
  Bot,
  Columns3,
  Copy,
  FileText,
  FilterX,
  FolderInput,
  Pencil,
  Plus,
  RotateCcw,
  SlidersHorizontal,
  Trash2,
  Upload,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import {
  Button,
  Card,
  CaseStatusBadge,
  ConfirmDialog,
  EmptyState,
  EntityKey,
  ErrorState,
  LabelChip,
  Menu,
  MenuCheckboxItem,
  MenuItem,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  Pagination,
  PriorityBadge,
  SearchField,
  SeverityBadge,
  SkeletonRows,
  Table,
  TableWrap,
  ToolbarMenu,
  Td,
  Th,
  Tr,
} from '../../components/ui';
import { useEnumLabels, useErrorText, useFormat, useT, type MessageKey } from '../../i18n';
import { useDebounced } from '../../lib/use-debounced';
import { usePermissions } from '../auth/permissions';
import { FolderTree, type FolderSelection } from '../folders/FolderTree';
import { useLabels } from '../labels/api';
import { ImportCasesDialog } from './import/ImportCasesDialog';
import { MoveCaseDialog } from './MoveCaseDialog';
import { useDeleteTestCase, useDuplicateTestCase, useTestCases } from './api';
import { CASE_COLUMNS, CASE_COLUMN_LABELS, useCaseColumns } from './columns';

type SortValue = `${TestCaseSortField}:${'asc' | 'desc'}`;

/** Radix radio groups work on strings, so "no filter" needs its own value. */
const ANY = 'any';

const SORT_OPTIONS: Array<{ value: SortValue; labelKey: MessageKey }> = [
  { value: 'seqNumber:desc', labelKey: 'cases.sortNewest' },
  { value: 'seqNumber:asc', labelKey: 'cases.sortOldest' },
  { value: 'title:asc', labelKey: 'cases.sortTitleAsc' },
  { value: 'title:desc', labelKey: 'cases.sortTitleDesc' },
  { value: 'updatedAt:desc', labelKey: 'cases.sortUpdated' },
];

export const TestCasesPage = ({ projectCode }: { projectCode: string }) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { t } = useT();
  const { relative } = useFormat();
  const { priority: priorityLabel, caseStatus, automation } = useEnumLabels();
  const { errorText } = useErrorText();
  const columns = useCaseColumns();

  const [folder, setFolder] = useState<FolderSelection>('all');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [priority, setPriority] = useState<CasePriority | ''>('');
  const [status, setStatus] = useState<CaseStatus | ''>('');
  const [labelId, setLabelId] = useState('');
  const [sort, setSort] = useState<SortValue>('seqNumber:desc');
  const [pendingDelete, setPendingDelete] = useState<{ id: string; caseKey: string } | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState<{
    id: string;
    caseKey: string;
    folderId: string;
  } | null>(null);

  const debouncedSearch = useDebounced(search);
  const [sortField, sortOrder] = sort.split(':') as [TestCaseSortField, 'asc' | 'desc'];
  const activeFilterCount = (priority ? 1 : 0) + (status ? 1 : 0) + (labelId ? 1 : 0);
  const activeSort = SORT_OPTIONS.find((option) => option.value === sort) ?? SORT_OPTIONS[0]!;

  const { data, isPending, error, refetch } = useTestCases(projectCode, {
    page,
    ...(folder === 'all' ? {} : { folderId: folder, includeSubfolders: true }),
    ...(priority ? { priority } : {}),
    ...(status ? { status } : {}),
    ...(labelId ? { labelId } : {}),
    ...(debouncedSearch ? { q: debouncedSearch } : {}),
    sort: sortField,
    order: sortOrder,
  });

  const { data: labels } = useLabels(projectCode);
  const duplicate = useDuplicateTestCase(projectCode);
  const remove = useDeleteTestCase(projectCode);

  const resetPage = () => setPage(1);

  const onDuplicate = async (caseId: string) => {
    try {
      const copy = await duplicate.mutateAsync(caseId);
      toast.success(t('cases.duplicated', { key: copy.caseKey }));
    } catch (duplicateError) {
      toast.error(errorText(duplicateError, 'cases.duplicateFailed'));
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync(pendingDelete.id);
      toast.success(t('cases.deleted', { key: pendingDelete.caseKey }));
      setPendingDelete(null);
    } catch (deleteError) {
      toast.error(errorText(deleteError, 'cases.deleteFailed'));
    }
  };

  const hasFilters = Boolean(debouncedSearch || priority || status) || folder !== 'all';

  return (
    <>
      <PageHeader
        title={t('cases.title')}
        description={t('cases.subtitle')}
        actions={
          can('case:write') ? (
            <>
              <Button variant="secondary" onClick={() => setImportOpen(true)}>
                <Upload className="size-4" />
                {t('cases.import')}
              </Button>
              <Button
                variant="primary"
                onClick={() =>
                  void navigate({
                    to: '/projects/$projectCode/test-cases/new',
                    params: { projectCode },
                  })
                }
              >
                <Plus className="size-4" />
                {t('cases.new')}
              </Button>
            </>
          ) : null
        }
      />

      <div className="flex flex-col gap-4 lg:flex-row">
        <FolderTree
          projectCode={projectCode}
          selected={folder}
          onSelect={(selection) => {
            setFolder(selection);
            resetPage();
          }}
          total={data?.meta.total ?? 0}
          kind="case"
          allLabel={t('folders.allCases')}
        />

        <Card className="min-w-0 flex-1">
          <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
            <SearchField
              value={search}
              onChange={(value) => {
                setSearch(value);
                resetPage();
              }}
              placeholder={t('cases.searchPlaceholder')}
              label={t('cases.searchLabel')}
            />

            <ToolbarMenu
              icon={<SlidersHorizontal className="size-3.5" />}
              label={t('common.filters')}
              badge={activeFilterCount}
              compact
            >
              <MenuLabel>{t('enum.priorityLabel')}</MenuLabel>
              <MenuRadioGroup
                value={priority === '' ? ANY : priority}
                onValueChange={(value) => {
                  setPriority(value === ANY ? '' : (value as CasePriority));
                  resetPage();
                }}
              >
                <MenuRadioItem value={ANY} keepOpen>
                  {t('cases.allPriorities')}
                </MenuRadioItem>
                {CASE_PRIORITIES.map((value) => (
                  <MenuRadioItem key={value} value={value} keepOpen>
                    {priorityLabel(value)}
                  </MenuRadioItem>
                ))}
              </MenuRadioGroup>

              <MenuSeparator />

              <MenuLabel>{t('common.status')}</MenuLabel>
              <MenuRadioGroup
                value={status === '' ? ANY : status}
                onValueChange={(value) => {
                  setStatus(value === ANY ? '' : (value as CaseStatus));
                  resetPage();
                }}
              >
                <MenuRadioItem value={ANY} keepOpen>
                  {t('cases.allStatuses')}
                </MenuRadioItem>
                {CASE_STATUSES.map((value) => (
                  <MenuRadioItem key={value} value={value} keepOpen>
                    {caseStatus(value)}
                  </MenuRadioItem>
                ))}
              </MenuRadioGroup>

              <MenuSeparator />

              <MenuLabel>{t('labels.title')}</MenuLabel>
              <MenuRadioGroup
                value={labelId === '' ? ANY : labelId}
                onValueChange={(value) => {
                  setLabelId(value === ANY ? '' : value);
                  resetPage();
                }}
              >
                <MenuRadioItem value={ANY} keepOpen>
                  {t('labels.allLabels')}
                </MenuRadioItem>
                {(labels ?? []).map((label) => (
                  <MenuRadioItem key={label.id} value={label.id} keepOpen>
                    {label.name}
                  </MenuRadioItem>
                ))}
              </MenuRadioGroup>

              {activeFilterCount > 0 ? (
                <>
                  <MenuSeparator />
                  <MenuItem
                    onSelect={() => {
                      setPriority('');
                      setStatus('');
                      setLabelId('');
                      resetPage();
                    }}
                  >
                    <FilterX className="size-3.5" />
                    {t('common.resetFilters')}
                  </MenuItem>
                </>
              ) : null}
            </ToolbarMenu>

            <ToolbarMenu
              icon={<Columns3 className="size-3.5" />}
              label={t('cases.columns')}
              compact
            >
              <MenuLabel>{t('cases.columns')}</MenuLabel>
              {CASE_COLUMNS.map((column) => (
                <MenuCheckboxItem
                  key={column}
                  checked={columns.isVisible(column)}
                  onCheckedChange={(checked) => columns.toggle(column, checked)}
                >
                  {t(CASE_COLUMN_LABELS[column])}
                </MenuCheckboxItem>
              ))}
              {columns.isDefault ? null : (
                <>
                  <MenuSeparator />
                  <MenuItem onSelect={columns.reset}>
                    <RotateCcw className="size-3.5" />
                    {t('cases.resetColumns')}
                  </MenuItem>
                </>
              )}
            </ToolbarMenu>

            <ToolbarMenu
              icon={<ArrowDownUp className="size-3.5" />}
              label={t(activeSort.labelKey)}
              compact
            >
              <MenuLabel>{t('common.sort')}</MenuLabel>
              <MenuRadioGroup
                value={sort}
                onValueChange={(value) => setSort(value as SortValue)}
              >
                {SORT_OPTIONS.map((option) => (
                  <MenuRadioItem key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </MenuRadioItem>
                ))}
              </MenuRadioGroup>
            </ToolbarMenu>
          </div>

          {error ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : isPending ? (
            <SkeletonRows />
          ) : data.data.length === 0 ? (
            <EmptyState
              icon={<FileText className="size-6" />}
              title={hasFilters ? t('cases.emptyFiltered') : t('cases.empty')}
              description={hasFilters ? t('cases.emptyFilteredHint') : t('cases.emptyHint')}
              action={
                can('case:write') && !hasFilters ? (
                  <Button
                    variant="primary"
                    onClick={() =>
                      void navigate({
                        to: '/projects/$projectCode/test-cases/new',
                        params: { projectCode },
                      })
                    }
                  >
                    <Plus className="size-4" />
                    {t('cases.create')}
                  </Button>
                ) : null
              }
            />
          ) : (
            <>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      {/* Key and title identify the row, so they are not
                          optional; everything else follows the reader's own
                          column choice. */}
                      <Th className="w-24">{t('cases.columnKey')}</Th>
                      <Th className="w-full">{t('common.title')}</Th>
                      {CASE_COLUMNS.filter(columns.isVisible).map((column) => (
                        <Th key={column}>{t(CASE_COLUMN_LABELS[column])}</Th>
                      ))}
                      <Th className="w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.map((testCase) => (
                      <Tr
                        key={testCase.id}
                        onClick={() =>
                          void navigate({
                            to: '/projects/$projectCode/test-cases/$caseKey',
                            params: { projectCode, caseKey: testCase.caseKey },
                          })
                        }
                      >
                        <Td>
                          <EntityKey>{testCase.caseKey}</EntityKey>
                        </Td>
                        <Td>
                          <Link
                            to="/projects/$projectCode/test-cases/$caseKey"
                            params={{ projectCode, caseKey: testCase.caseKey }}
                            className="font-medium text-ink hover:text-accent-fg"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {testCase.title}
                          </Link>
                        </Td>
                        {columns.isVisible('folder') ? (
                          <Td className="text-ink-muted">
                            {testCase.folderName ?? t('common.empty')}
                          </Td>
                        ) : null}
                        {columns.isVisible('status') ? (
                          <Td>
                            <CaseStatusBadge status={testCase.status} />
                          </Td>
                        ) : null}
                        {columns.isVisible('labels') ? (
                          <Td>
                            {testCase.labels.length === 0 ? (
                              <span className="text-ink-subtle">{t('common.empty')}</span>
                            ) : (
                              <span className="flex flex-wrap gap-1">
                                {testCase.labels.map((label) => (
                                  <LabelChip key={label.id} label={label} />
                                ))}
                              </span>
                            )}
                          </Td>
                        ) : null}
                        {columns.isVisible('environment') ? (
                          <Td className="text-ink-muted">
                            {testCase.environment?.name ?? t('common.empty')}
                          </Td>
                        ) : null}
                        {columns.isVisible('layer') ? (
                          <Td className="text-ink-muted">
                            {testCase.layer?.name ?? t('common.empty')}
                          </Td>
                        ) : null}
                        {columns.isVisible('module') ? (
                          <Td className="text-ink-muted">
                            {testCase.module?.name ?? t('common.empty')}
                          </Td>
                        ) : null}
                        {columns.isVisible('priority') ? (
                          <Td>
                            <PriorityBadge priority={testCase.priority} />
                          </Td>
                        ) : null}
                        {columns.isVisible('severity') ? (
                          <Td>
                            <SeverityBadge severity={testCase.severity} />
                          </Td>
                        ) : null}
                        {columns.isVisible('automated') ? (
                          <Td className="text-ink-muted">
                            <span className="flex items-center gap-1.5">
                              {testCase.isAutomated ? (
                                <Bot className="size-3.5 text-accent-fg" aria-hidden="true" />
                              ) : null}
                              {automation(testCase.isAutomated)}
                            </span>
                          </Td>
                        ) : null}
                        {columns.isVisible('author') ? (
                          <Td className="text-ink-muted">
                            {testCase.createdBy?.name ?? t('common.empty')}
                          </Td>
                        ) : null}
                        {columns.isVisible('updated') ? (
                          <Td className="text-ink-muted">{relative(testCase.updatedAt)}</Td>
                        ) : null}
                        <Td>
                          <div onClick={(event) => event.stopPropagation()}>
                            {can('case:write') ? (
                              <Menu>
                                <MenuItem
                                  onSelect={() =>
                                    void navigate({
                                      to: '/projects/$projectCode/test-cases/$caseKey/edit',
                                      params: { projectCode, caseKey: testCase.caseKey },
                                    })
                                  }
                                >
                                  <Pencil className="size-3.5" />
                                  {t('common.edit')}
                                </MenuItem>
                                <MenuItem
                                  onSelect={() =>
                                    setPendingMove({
                                      id: testCase.id,
                                      caseKey: testCase.caseKey,
                                      folderId: testCase.folderId,
                                    })
                                  }
                                >
                                  <FolderInput className="size-3.5" />
                                  {t('cases.moveAction')}
                                </MenuItem>
                                <MenuItem onSelect={() => void onDuplicate(testCase.id)}>
                                  <Copy className="size-3.5" />
                                  {t('common.duplicate')}
                                </MenuItem>
                                {can('case:delete') ? (
                                  <MenuItem
                                    destructive
                                    onSelect={() =>
                                      setPendingDelete({
                                        id: testCase.id,
                                        caseKey: testCase.caseKey,
                                      })
                                    }
                                  >
                                    <Trash2 className="size-3.5" />
                                    {t('common.delete')}
                                  </MenuItem>
                                ) : null}
                              </Menu>
                            ) : null}
                          </div>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
              <Pagination meta={data.meta} onPageChange={setPage} />
            </>
          )}
        </Card>
      </div>

      <ImportCasesDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        projectCode={projectCode}
        defaultFolderId={folder === 'all' ? undefined : folder}
      />

      <MoveCaseDialog
        projectCode={projectCode}
        testCase={pendingMove}
        onClose={() => setPendingMove(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={t('cases.deleteTitle', { key: pendingDelete?.caseKey ?? '' })}
        description={t('cases.deleteHint')}
        confirmLabel={t('cases.deleteAction')}
        loading={remove.isPending}
        onConfirm={() => void confirmDelete()}
      />
    </>
  );
};
